import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import NewDistributionListDialog from 'components/Notifications/NewDistributionListDialog';
import EditDistributionListDialog from 'components/Notifications/EditDistributionListDialog';
import NewICSTeamDialog from 'components/ICSTeams/NewICSTeamDialog';
import EditICSTeamDialog from 'components/ICSTeams/EditICSTeamDialog';
import {
  deleteDistributionList,
  editDistributionList,
  createDistributionList,
  fetchAllDistributionList,
} from 'actions/notificationActions';
import { fetchRostersForGroups } from '../../actions/roleActions';
import { StylishConfirmDialog } from 'components/DesignSystems/New/StylishConfirmDialog';
import TeamCard from 'components/DesignSystems/New/TeamCard';
import { ICSTeam } from '../../types/teams';
import { AreaOfResponsibility } from '../../types/aor';

const recipientTypes = [
  'Email',
  'SMS',
  'Pager',
  'Text-To-Speech (cell)',
  'Text-To-Speech (home)',
];

type ListObj = {
  id: string;
  name: string;
  description?: string;
  aor?: {
    id: string;
    name: string;
  } | null;
  type?: 'functional' | 'ics';
};

type RowObj = {
  id: string;
  name: string;
  members: string[];
  nonMembers: { id: string; content: string }[];
  description?: string;
  aor: AreaOfResponsibility | null;
  type?: 'functional' | 'ics';
};

const OrgTeams: React.FC = () => {
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState<'functional' | 'ics'>(
    'functional'
  );
  const [isNewTeamDialogOpen, setIsNewTeamDialogOpen] = useState<boolean>();
  const [isEditTeamDialogOpen, setIsEditTeamDialogOpen] = useState<any>();
  const [isNewICSTeamDialogOpen, setIsNewICSTeamDialogOpen] =
    useState<boolean>();
  const [selectedItem, setSelectedItem] = useState<RowObj | undefined>();
  const [distributionLists, setDistributionLists] = useState([]);
  const [showConfirmDialog, setShowConfirmDialog] = useState<string | null>(
    null
  );

  const validateRBACPermission = useSelector((state: any) => {
    return state.app.validateRBACPermissionForActionResult;
  });

  const selectedGroup = useSelector((state: any) => {
    return state.app.currentlySelectedGroup;
  });

  const distributionListItems = useSelector((state: any) => {
    return state.app.distributionListItems;
  });

  useEffect(() => {
    if (selectedGroup) {
      dispatch(fetchAllDistributionList(selectedGroup.group_guid));
      dispatch(fetchRostersForGroups([selectedGroup.group_guid]));
    }
  }, [selectedGroup, dispatch]);

  useEffect(() => {
    setDistributionLists(distributionListItems);
  }, [distributionListItems]);

  const handleNewTeam = (list: ListObj) => {
    if (list) {
      const teamData = {
        ...list,
        type: activeTab === 'functional' ? 'functional' : 'ics',
      };
      dispatch(createDistributionList(teamData));
    }
    setIsNewTeamDialogOpen(false);
  };

  const handleEditTeam = (list: ListObj) => {
    if (list) {
      const teamData = {
        ...list,
        type: activeTab === 'functional' ? 'functional' : 'ics',
      };
      dispatch(editDistributionList(teamData));
    }
    setIsEditTeamDialogOpen(false);
  };

  const handleNewICSTeam = (team?: ICSTeam) => {
    if (team) {
      const teamData = {
        id: team.id,
        distribution_list_id: team.id,
        name: team.name,
        description: null,
        group_guid: selectedGroup.group_guid,
        aor: team.aor,
        type: 'ics',
        members: team.members,
        nonMembers: team.nonMembers,
      };
      dispatch(createDistributionList(teamData));
    }
    setIsNewICSTeamDialogOpen(false);
  };

  const handleEditICSTeam = (team?: ICSTeam) => {
    if (team) {
      const teamData = {
        id: team.id,
        distribution_list_id: team.id,
        name: team.name,
        description: null,
        group_guid: selectedGroup.group_guid,
        aor: team.aor,
        type: 'ics',
        members: team.members,
        nonMembers: team.nonMembers,
      };
      dispatch(editDistributionList(teamData));
    }
    setSelectedItem(undefined);
  };

  const handleTeamDeleteClick = (idToDelete: string) => {
    dispatch(deleteDistributionList(idToDelete));
    setShowConfirmDialog(null);
  };

  const handleTeamEditClick = (item: RowObj) => {
    if (activeTab === 'functional') {
      setIsEditTeamDialogOpen(item);
    } else {
      setSelectedItem(item);
    }
  };

  const roster = useSelector((state: any) => {
    let rosters = state.app.rostersForGroups;
    if (Array.isArray(rosters) && rosters.length) {
      rosters = rosters
        .filter((member) => {
          return member.group_guid === selectedGroup.group_guid;
        })
        .map((member) => {
          return {
            ...member,
            role_string: member.role_assignments.reduce(
              (acc: string, item: { name: string }) => {
                acc = acc ? acc + ', ' + item.name : item.name;
                return acc;
              },
              ''
            ),
          };
        });
    }
    return rosters || [];
  });

  const filteredTeams = distributionLists.filter((team: any) => {
    if (activeTab === 'functional') {
      return team.type === 'functional' || !team.type;
    } else {
      return team.type === 'ics';
    }
  });

  const renderTeams = (teams: any[]) => {
    return teams.map((team: any) => {
      const memberObjs = (team.members || [])
        .map((id: string) => roster.find((r: any) => r.user_guid === id))
        .filter(Boolean)
        .map((user: any) => ({
          id: user.user_guid,
          name: user.user_name || user.email_address,
        }));

      const nonMemberObjs = (team.nonMembers || []).map((m: any) => ({
        id: m.id,
        name: m.content,
      }));
      const allMembers = [...memberObjs, ...nonMemberObjs];
      const totalMembers =
        (team.members?.length || 0) + (team.nonMembers?.length || 0);

      const description =
        activeTab === 'functional' && !team.description
          ? '-'
          : team.description;

      return (
        <div className="col-12 col-lg-6 col-xl-4" key={team.id}>
          <TeamCard
            name={team.name}
            description={description}
            aor={team.aor?.name}
            members={allMembers}
            totalMembers={totalMembers}
            onEdit={() => handleTeamEditClick(team)}
            onDelete={() => setShowConfirmDialog(team.id)}
          />
        </div>
      );
    });
  };

  return (
    <>
      <div className="d-md-flex align-items-center mb-3">
        <div className="d-flex align-items-center flex-grow-1">
          <div className="custom-tabs">
            <button
              className={`custom-tab-btn${activeTab === 'functional' ? ' active' : ''}`}
              onClick={() => setActiveTab('functional')}
              type="button"
            >
              Functional Team
            </button>
            <button
              className={`custom-tab-btn${activeTab === 'ics' ? ' active' : ''}`}
              onClick={() => setActiveTab('ics')}
              type="button"
            >
              ICS Team
            </button>
          </div>
        </div>
        {validateRBACPermission['Create Role'] ? (
          <>
            <StylishNewButton
              className={`button--primary ms-auto mt-2 mt-md-0`}
              onClick={() => {
                if (activeTab === 'functional') {
                  setIsNewTeamDialogOpen(true);
                } else {
                  setIsNewICSTeamDialogOpen(true);
                }
              }}
            >
              {activeTab === 'functional' ? 'New Team' : 'New ICS Team'}
            </StylishNewButton>
          </>
        ) : (
          <>
            <div className={'ms-auto text-end'}>
              <StylishNewButton
                className={`button--primary mt-2 mt-md-0`}
                disabled
              >
                {activeTab === 'functional' ? 'New Team' : 'New ICS Team'}
              </StylishNewButton>
              <span className="form-text form-error">
                You don&apos;t have permission.
              </span>
            </div>
          </>
        )}
      </div>
      <div className="row g-4 g-xl-5 justify-content-start">
        {renderTeams(filteredTeams)}
      </div>

      {isNewTeamDialogOpen && (
        <NewDistributionListDialog
          show={isNewTeamDialogOpen}
          roster={roster}
          onClose={(list: ListObj) => handleNewTeam(list)}
          recipientTypes={recipientTypes}
          distributionLists={distributionLists}
        />
      )}

      {isEditTeamDialogOpen && (
        <EditDistributionListDialog
          roster={roster}
          onClose={(list: ListObj) => handleEditTeam(list)}
          selectedDistributionList={isEditTeamDialogOpen}
          distributionLists={distributionLists}
          show={isEditTeamDialogOpen}
        />
      )}

      {isNewICSTeamDialogOpen && (
        <NewICSTeamDialog
          show={isNewICSTeamDialogOpen}
          roster={roster}
          onClose={handleNewICSTeam}
          icsTeams={filteredTeams}
          teamAor={selectedGroup.aor}
        />
      )}

      {!!selectedItem && (
        <EditICSTeamDialog
          roster={roster}
          onClose={handleEditICSTeam}
          selectedICSTeam={selectedItem}
          icsTeams={filteredTeams}
          show={!!selectedItem}
        />
      )}

      {!!showConfirmDialog && (
        <StylishConfirmDialog
          show={!!showConfirmDialog}
          dialogTitle={'Delete This Team'}
          dialogContent={'Are you sure you want to delete this team?'}
          onClose={() => setShowConfirmDialog(null)}
          onConfirm={() => handleTeamDeleteClick(showConfirmDialog)}
        />
      )}
    </>
  );
};

export default OrgTeams;
